import { Injectable } from '@angular/core';
import { InAppBrowser, InAppBrowserEvent, InAppBrowserObject } from '@ionic-native/in-app-browser';
import { SocialSharing } from '@ionic-native/social-sharing';
import { TranslateService } from '@ngx-translate/core';
import { Platform } from 'ionic-angular';
import { APP_CONFIG } from '../../environment';
import { AnalyticsEventType } from '../../model/types/analytics.const';
import {
  BridgePartnerCheckoutPartner,
  BridgePartnerCheckoutRequest,
  BridgePartnerCheckoutResult,
  BridgePartnerCheckoutStatus
} from '../../model/types/bridge-types';
import { SegmentProvider } from '../analytics/segment';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import { buildDefaultIabOptions } from '../route/iab-options';

// Sent by the Ria-hosted checkout page through `cordova_iab.postMessage`.
// `ready`: the hosted page loaded, with its `version`. `frame-load`: the partner frame loaded.
// `status`: the partner showed a successful purchase, but the checkout stays open on its
// receipt. `open-url`: the partner asked to open a (help) link. `download`: the image of the
// partner's receipt, as a data URL. `error`: a script error on the hosted page. `result`: the
// checkout finished.
interface PartnerCheckoutPageMessage {
  source: string;
  type: 'ready' | 'frame-load' | 'status' | 'open-url' | 'download' | 'error' | 'result';
  status?: string;
  params?: { [key: string]: any };
  url?: string;
  version?: string;
  message?: string;
}

interface PartnerCheckoutMessageEvent extends InAppBrowserEvent {
  data: PartnerCheckoutPageMessage;
}

// One in-app browser event of a checkout attempt, `ms` after the open.
interface PartnerCheckoutAttemptEvent {
  ms: number;
  event: string;
  [detail: string]: string | number;
}

type PartnerCheckoutLoadEventType = 'loadstart' | 'loadstop' | 'loaderror';

const ATTEMPT_LOG_MESSAGE = 'Partner checkout: attempt';
const MAX_LOGGED_TEXT_LENGTH = 200;

// Collects the in-app browser events of one checkout, so its close writes one log line that
// shows how far the checkout got.
class PartnerCheckoutAttemptLog {
  private readonly startedAt = Date.now();
  private readonly events: PartnerCheckoutAttemptEvent[] = [];
  private droppedEvents = 0;
  private readyMs: number = null;

  constructor(private partner: BridgePartnerCheckoutPartner, private isHosted: boolean) {
  }

  // Returns the recorded details, so a load error can be logged with the same values.
  recordLoad(type: PartnerCheckoutLoadEventType, event: InAppBrowserEvent): { [detail: string]: any } {
    const details: { [detail: string]: any } = { event: type, url: PartnerCheckoutProvider.describeUrl(event ? event.url : null) };
    if (type === 'loaderror') {
      details.code = event ? event.code : null;
      details.message = event && typeof event.message === 'string' ? event.message.substring(0, MAX_LOGGED_TEXT_LENGTH) : null;
    }
    this.record(details);
    return details;
  }

  // Returns false when the message does not come from the hosted page.
  recordMessage(message: PartnerCheckoutPageMessage): boolean {
    if (!message || message.source !== PartnerCheckoutProvider.PAGE_MESSAGE_SOURCE) {
      this.record({ event: 'message-rejected' });
      return false;
    }
    this.record(PartnerCheckoutProvider.describePageMessage(message));
    return true;
  }

  record(details: { [detail: string]: string | number }) {
    if (this.events.length >= PartnerCheckoutProvider.MAX_ATTEMPT_EVENTS) {
      this.droppedEvents++;
      return;
    }
    this.events.push(Object.assign({ ms: this.elapsedMs() }, details) as PartnerCheckoutAttemptEvent);
  }

  markReady() {
    if (this.readyMs === null) {
      this.readyMs = this.elapsedMs();
    }
  }

  // A warning when the checkout failed or its page never loaded, so production logs keep them.
  write(source: any, result: BridgePartnerCheckoutResult, closedByApp: boolean) {
    const loaded = this.isHosted ? this.readyMs !== null : this.events.some(entry => entry.event === 'loadstop');
    const details = {
      partner: this.partner,
      mode: this.isHosted ? 'hosted' : 'direct',
      status: result.status,
      reason: result.params[PartnerCheckoutProvider.RESULT_REASON_PARAM] || null,
      closedBy: closedByApp ? 'app' : 'user',
      readyMs: this.readyMs,
      durationMs: this.elapsedMs(),
      events: this.events,
      droppedEvents: this.droppedEvents
    };
    if (result.status === 'failed' || !loaded) {
      LoggerProvider.warning(source, LoggerEvent.Vue, ATTEMPT_LOG_MESSAGE, details);
    } else {
      LoggerProvider.info(source, ATTEMPT_LOG_MESSAGE, details);
    }
  }

  private elapsedMs(): number {
    return Date.now() - this.startedAt;
  }
}

/**
 * Opens a partner checkout (e.g. Ding) in the in-app browser.
 * Production `frame-ancestors` rules out an iframe inside the app web view (only staging
 * allows localhost), and the app web view injects scripts, which disables Apple Pay there.
 * SafariViewController / the system browser emit no events, so only the IAB can report back.
 * Hosted mode reports through the IAB `message` event (the partner sits in an iframe, which
 * fires no `loadstart`); direct mode reports through the return URL on `loadstart`.
 * Never inject scripts (executeScript / insertCSS): on iOS that disables Apple Pay in WKWebView.
 */
@Injectable()
export class PartnerCheckoutProvider {
  static readonly RESULT_STATUS_PARAM = 'status';
  static readonly RESULT_REASON_PARAM = 'reason';
  static readonly LOAD_ERROR_REASON = 'load_error';
  static readonly LOAD_TIMEOUT_REASON = 'load_timeout';
  static readonly PAGE_MESSAGE_SOURCE = 'ria-partner-checkout';
  static readonly WEB_POLL_INTERVAL_MS = 1000;
  static readonly HOSTED_READY_TIMEOUT_MS = 15000;
  static readonly DEFAULT_PRODUCT_VARIANT = 'RMT5';
  static readonly MAX_ATTEMPT_EVENTS = 30;

  // Not failures: iOS cancels a load we replaced or closed (-999) or handed off (102).
  private static readonly IGNORED_LOAD_ERROR_CODES = [-999, 102];

  private static readonly RESULT_STATUSES: BridgePartnerCheckoutStatus[] = ['success', 'cancelled', 'failed', 'expired'];
  private static readonly PAGE_MESSAGE_TYPES = ['ready', 'frame-load', 'status', 'open-url', 'download', 'error', 'result'];
  private static readonly PAGE_VERSION_PATTERN = /^[\w.-]{1,20}$/;
  // https only: on Android the IAB reports a custom scheme only through `customscheme` with the
  // `AllowedSchemes` preference, which the app does not set.
  private static readonly RETURN_URL_PATTERN = /^https:\/\/([a-z0-9-]+\.)*riamoneytransfer\.com\//i;
  private static readonly HOSTED_URL_PATTERN = /^https:\/\/([a-z0-9-]+\.)*riamoneytransfer\.com\//i;
  private static readonly EXTERNAL_URL_PATTERN = /^https:\/\//i;
  // Ding's receipt is a PNG data URL. Only a data URL of 5 MB or less goes to the share sheet.
  private static readonly RECEIPT_URL_PATTERN = /^data:image\/(png|jpeg);base64,/;
  private static readonly MAX_RECEIPT_URL_LENGTH = 5 * 1024 * 1024;

  private isOpen = false;
  // True while the share sheet shows a receipt, so a second `download` does not open a second sheet.
  private isSharingReceipt = false;

  constructor(
    private iab: InAppBrowser,
    private platform: Platform,
    private translate: TranslateService,
    private analytics: SegmentProvider,
    private socialSharing: SocialSharing
  ) {
  }

  static parseQuery(url: string): { [key: string]: string } {
    const params: { [key: string]: string } = {};
    const queryStart = url.indexOf('?');
    if (queryStart < 0) {
      return params;
    }
    let query = url.substring(queryStart + 1);
    const hashStart = query.indexOf('#');
    if (hashStart >= 0) {
      query = query.substring(0, hashStart);
    }
    query.split('&').forEach(pair => {
      if (!pair) {
        return;
      }
      const separator = pair.indexOf('=');
      let rawKey = pair;
      let rawValue = '';
      if (separator >= 0) {
        rawKey = pair.substring(0, separator);
        rawValue = pair.substring(separator + 1);
      }
      const key = PartnerCheckoutProvider.decode(rawKey);
      if (key) {
        params[key] = PartnerCheckoutProvider.decode(rawValue);
      }
    });
    return params;
  }

  static statusFromParams(params: { [key: string]: string }): BridgePartnerCheckoutStatus {
    const value = (params[PartnerCheckoutProvider.RESULT_STATUS_PARAM] || '').toLowerCase() as BridgePartnerCheckoutStatus;
    return PartnerCheckoutProvider.RESULT_STATUSES.indexOf(value) >= 0 ? value : 'failed';
  }

  // The bridge is open to every frame, so keep only string values and never trust the status.
  static resultFromPageMessage(message: PartnerCheckoutPageMessage): BridgePartnerCheckoutResult {
    const params: { [key: string]: string } = {};
    const source = message.params && typeof message.params === 'object' ? message.params : {};
    Object.keys(source).forEach(key => {
      if (typeof source[key] === 'string') {
        params[key] = source[key];
      }
    });
    params[PartnerCheckoutProvider.RESULT_STATUS_PARAM] = typeof message.status === 'string' ? message.status : '';
    return { status: PartnerCheckoutProvider.statusFromParams(params), params };
  }

  // The token travels in the query (direct mode) or the fragment (hosted mode), so logs keep
  // only the origin and the path.
  static describeUrl(url: string): string {
    return typeof url === 'string' ? url.split(/[?#]/)[0] : null;
  }

  // Any frame in the in-app browser can post a message, so only known values reach the logs.
  static describePageMessage(message: PartnerCheckoutPageMessage): { [detail: string]: string | number } {
    const type = PartnerCheckoutProvider.PAGE_MESSAGE_TYPES.indexOf(message.type) >= 0 ? message.type : 'other';
    const details: { [detail: string]: string | number } = { event: 'message', type };
    if (type === 'ready' && typeof message.version === 'string' && PartnerCheckoutProvider.PAGE_VERSION_PATTERN.test(message.version)) {
      details.version = message.version;
    } else if (type === 'status' || type === 'result') {
      const status = message.status as BridgePartnerCheckoutStatus;
      details.status = PartnerCheckoutProvider.RESULT_STATUSES.indexOf(status) >= 0 ? status : 'other';
    } else if (type === 'error' && typeof message.message === 'string') {
      details.error = message.message.substring(0, MAX_LOGGED_TEXT_LENGTH);
    } else if (type === 'download' && typeof message.url === 'string') {
      // The receipt shows the phone number and the amount: log its size, never the image.
      details.length = message.url.length;
    }
    return details;
  }

  private static decode(value: string): string {
    try {
      return decodeURIComponent(value.replace(/\+/g, ' '));
    } catch (ex) {
      return value;
    }
  }

  static buildCheckoutUrl(request: BridgePartnerCheckoutRequest): string {
    const separator = request.url.indexOf('?') >= 0 ? '&' : '?';
    return `${request.url}${separator}token=${encodeURIComponent(request.token)}`;
  }

  // The hosted page reads both from the fragment, which never reaches a server or access log.
  static buildHostedCheckoutUrl(hostedUrl: string, request: BridgePartnerCheckoutRequest): string {
    return `${hostedUrl}#checkout=${encodeURIComponent(request.url)}&token=${encodeURIComponent(request.token)}`;
  }

  isValidRequest(request: BridgePartnerCheckoutRequest): boolean {
    if (!request || !request.url || !request.returnUrlPrefix || !request.token) {
      return false;
    }
    const baseUrl = this.getBaseUrl(request.partner);
    return !!baseUrl
      && request.url.indexOf(baseUrl) === 0
      && PartnerCheckoutProvider.RETURN_URL_PATTERN.test(request.returnUrlPrefix);
  }

  async open(request: BridgePartnerCheckoutRequest, productVariant: string = PartnerCheckoutProvider.DEFAULT_PRODUCT_VARIANT): Promise<BridgePartnerCheckoutResult> {
    if (!this.isValidRequest(request)) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'Partner checkout: rejected invalid request', { partner: request ? request.partner : null });
      return { status: 'failed', params: {} };
    }
    if (this.isOpen) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'Partner checkout: a checkout is already open', { partner: request.partner });
      return { status: 'failed', params: {} };
    }

    this.isOpen = true;
    LoggerProvider.info(this, 'Partner checkout: opening', { partner: request.partner });
    this.track(AnalyticsEventType.PartnerCheckoutOpened, productVariant, { partner: request.partner });
    try {
      let result: BridgePartnerCheckoutResult;
      if (this.platform.is('cordova')) {
        result = await this.openInAppBrowser(request);
      } else {
        result = await this.openWebWindow(request);
      }
      LoggerProvider.info(this, 'Partner checkout: finished', { partner: request.partner, status: result.status });
      this.track(AnalyticsEventType.PartnerCheckoutCompleted, productVariant, { partner: request.partner, status: result.status });
      return result;
    } finally {
      this.isOpen = false;
    }
  }

  private openInAppBrowser(request: BridgePartnerCheckoutRequest): Promise<BridgePartnerCheckoutResult> {
    return new Promise<BridgePartnerCheckoutResult>(resolve => {
      let result: BridgePartnerCheckoutResult = null;
      // Set by a `status` success message: the customer may close the IAB on the partner's receipt.
      let successResult: BridgePartnerCheckoutResult = null;
      let readyTimer: number = null;
      let browser: InAppBrowserObject;
      const isHosted = !!this.getHostedUrl(request.partner);
      const attempt = new PartnerCheckoutAttemptLog(request.partner, isHosted);
      try {
        browser = this.iab.create(this.getOpenUrl(request), '_blank', buildDefaultIabOptions(this.platform, this.translate));
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Partner checkout: could not open the in-app browser');
        resolve({ status: 'failed', params: {} });
        return;
      }

      const stopReadyTimer = () => {
        if (readyTimer !== null) {
          window.clearTimeout(readyTimer);
          readyTimer = null;
        }
      };
      const finish = (finalResult: BridgePartnerCheckoutResult) => {
        if (result) {
          return;
        }
        result = finalResult;
        stopReadyTimer();
        browser.close();
      };
      const fail = (reason: string) => finish({ status: 'failed', params: { [PartnerCheckoutProvider.RESULT_REASON_PARAM]: reason } });

      browser.on('loadstart').subscribe((event: InAppBrowserEvent) => {
        attempt.recordLoad('loadstart', event);
        if (result || !event || !event.url || event.url.indexOf(request.returnUrlPrefix) !== 0) {
          return;
        }
        const params = PartnerCheckoutProvider.parseQuery(event.url);
        finish({ status: PartnerCheckoutProvider.statusFromParams(params), params });
      });
      browser.on('loadstop').subscribe((event: InAppBrowserEvent) => attempt.recordLoad('loadstop', event));
      browser.on('message').subscribe((event: PartnerCheckoutMessageEvent) => {
        const message = event && event.data;
        if (!attempt.recordMessage(message) || result) {
          return;
        }
        if (message.type === 'ready') {
          attempt.markReady();
          stopReadyTimer();
        } else if (message.type === 'status' && message.status === 'success') {
          successResult = PartnerCheckoutProvider.resultFromPageMessage(message);
        } else if (message.type === 'open-url') {
          this.openExternalUrl(message.url);
        } else if (message.type === 'download') {
          this.shareReceipt(message.url);
        } else if (message.type === 'result') {
          finish(PartnerCheckoutProvider.resultFromPageMessage(message));
        }
      });
      browser.on('loaderror').subscribe((event: InAppBrowserEvent) => {
        const { url, code, message } = attempt.recordLoad('loaderror', event);
        if (result || PartnerCheckoutProvider.IGNORED_LOAD_ERROR_CODES.indexOf(code) >= 0) {
          return;
        }
        LoggerProvider.error(this, LoggerEvent.Vue, 'Partner checkout: load error', { partner: request.partner, code, message, url });
        fail(PartnerCheckoutProvider.LOAD_ERROR_REASON);
      });
      browser.on('exit').subscribe(() => {
        stopReadyTimer();
        // If the share plugin never calls back, the next checkout can still share a receipt.
        this.isSharingReceipt = false;
        attempt.record({ event: 'exit' });
        const finalResult = result || successResult || { status: 'closed', params: {} };
        attempt.write(this, finalResult, !!result);
        resolve(finalResult);
      });

      // An HTTP error page (e.g. an env without the hosted page) fires no loaderror: only the
      // hosted page's `ready` message proves it loaded. Direct mode has no such signal.
      if (isHosted) {
        readyTimer = window.setTimeout(() => {
          readyTimer = null;
          LoggerProvider.error(this, LoggerEvent.Vue, 'Partner checkout: hosted page not ready', { partner: request.partner });
          fail(PartnerCheckoutProvider.LOAD_TIMEOUT_REASON);
        }, PartnerCheckoutProvider.HOSTED_READY_TIMEOUT_MS);
      }
    });
  }

  // The share sheet shows over the checkout and lets the customer save the image of the receipt.
  private shareReceipt(url: string) {
    if (this.isSharingReceipt) {
      LoggerProvider.info(this, 'Partner checkout: ignored a receipt while the share sheet is open');
      return;
    }
    const image = typeof url === 'string' && url.length <= PartnerCheckoutProvider.MAX_RECEIPT_URL_LENGTH
      ? PartnerCheckoutProvider.RECEIPT_URL_PATTERN.exec(url)
      : null;
    if (!image) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'Partner checkout: rejected a receipt that is not an image');
      return;
    }
    const fileName = image[1] === 'jpeg' ? 'receipt.jpg' : 'receipt.png';
    this.isSharingReceipt = true;
    this.socialSharing.shareWithOptions({ files: [`df:${fileName};${url}`] }).then(
      result => {
        this.isSharingReceipt = false;
        LoggerProvider.info(this, 'Partner checkout: receipt share', { completed: !!(result && result.completed) });
      },
      ex => {
        this.isSharingReceipt = false;
        LoggerProvider.exception(this, ex, LoggerEvent.Vue, 'Partner checkout: could not share the receipt');
      });
  }

  // A second IAB cannot open over the checkout, so links go to the system browser.
  private openExternalUrl(url: string) {
    if (typeof url !== 'string' || !PartnerCheckoutProvider.EXTERNAL_URL_PATTERN.test(url)) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'Partner checkout: rejected a non-https link');
      return;
    }
    this.iab.create(url, '_system');
  }

  // Browser dev only: a cross-origin window exposes no navigation, so the only signal is it closing.
  private openWebWindow(request: BridgePartnerCheckoutRequest): Promise<BridgePartnerCheckoutResult> {
    return new Promise<BridgePartnerCheckoutResult>(resolve => {
      const popup = window.open(this.getOpenUrl(request), '_blank');
      if (!popup) {
        resolve({ status: 'failed', params: {} });
        return;
      }
      const timer = window.setInterval(() => {
        if (popup.closed) {
          window.clearInterval(timer);
          resolve({ status: 'closed', params: {} });
        }
      }, PartnerCheckoutProvider.WEB_POLL_INTERVAL_MS);
    });
  }

  // A Ria-hosted page that iframes the partner (allowed by its frame-ancestors) when configured,
  // otherwise the partner checkout itself, top-level.
  private getOpenUrl(request: BridgePartnerCheckoutRequest): string {
    const hostedUrl = this.getHostedUrl(request.partner);
    if (hostedUrl) {
      return PartnerCheckoutProvider.buildHostedCheckoutUrl(hostedUrl, request);
    }
    return PartnerCheckoutProvider.buildCheckoutUrl(request);
  }

  private getHostedUrl(partner: BridgePartnerCheckoutPartner): string {
    const hostedUrls: { [partner: string]: string } = {
      ding: APP_CONFIG.dingHostedCheckoutUrl
    };
    const hostedUrl = hostedUrls[partner];
    return hostedUrl && PartnerCheckoutProvider.HOSTED_URL_PATTERN.test(hostedUrl) ? hostedUrl : null;
  }

  private getBaseUrl(partner: BridgePartnerCheckoutPartner): string {
    const baseUrls: { [partner: string]: string } = {
      ding: APP_CONFIG.dingBaseUrl
    };
    return baseUrls[partner] || null;
  }

  private track(eventName: AnalyticsEventType, productVariant: string, analyticsData: any) {
    Promise.resolve(this.analytics.trackEvent({ eventName, analyticsData: Object.assign({ productVariant }, analyticsData) }))
      .catch(ex => LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Partner checkout: tracking failed'));
  }
}



// WEBPACK FOOTER //
// ./src/providers/partner-checkout/partner-checkout.ts